import * as Linking from 'expo-linking';
import * as StoreReview from 'expo-store-review';
import { useTranslation } from 'react-i18next';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useMemo } from 'react';
import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';

import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';

const STORAGE_KEY = 'level2_review_shown';

type Props = {
  visible: boolean;
  onClose: () => void;
};

export function Level2ReviewModal({ visible, onClose }: Props) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  async function handleRate() {
    const available = await StoreReview.isAvailableAsync();
    if (available) {
      await StoreReview.requestReview();
    } else {
      await Linking.openURL('market://details?id=com.heirapsia.app');
    }
    await AsyncStorage.setItem(STORAGE_KEY, '1');
    onClose();
  }

  async function handleSuggest() {
    await Linking.openURL(
      `mailto:hello@heirapsia.gr?subject=${encodeURIComponent(t('level2ReviewModal.suggestSubject'))}`,
    );
    await AsyncStorage.setItem(STORAGE_KEY, '1');
    onClose();
  }

  function handleLater() {
    onClose();
  }

  return (
    <Modal visible={visible} transparent animationType="fade" statusBarTranslucent onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View style={styles.card}>
          <Text style={styles.emoji}>⭐</Text>
          <Text style={styles.title}>{t('common.enjoyApp')}</Text>
          <Text style={styles.body}>
            {t('reviewModal.body')}
          </Text>
          <Pressable style={[styles.btn, { backgroundColor: colors.primary }]} onPress={() => void handleRate()}>
            <Text style={styles.btnText}>{t('settings.rate_app')}</Text>
          </Pressable>
          <Pressable style={[styles.btn, styles.btnSecondary, { borderColor: colors.border }]} onPress={() => void handleSuggest()}>
            <Text style={[styles.btnText, { color: colors.text }]}>{t('settings.suggest_feature')}</Text>
          </Pressable>
          <Pressable style={styles.laterBtn} onPress={handleLater}>
            <Text style={styles.laterText}>{t('common.maybeLater')}</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}

export { STORAGE_KEY as LEVEL2_REVIEW_KEY };

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    backdrop: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.6)',
      alignItems: 'center',
      justifyContent: 'center',
      padding: spacing.xl,
    },
    card: {
      backgroundColor: colors.surface,
      borderRadius: radius.xl,
      padding: spacing.xl,
      alignItems: 'center',
      gap: spacing.sm,
      width: '100%',
      maxWidth: 320,
    },
    emoji: { fontSize: 48 },
    title: {
      color: colors.text,
      fontSize: fontSize.xl,
      fontWeight: '700',
      textAlign: 'center',
    },
    body: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      textAlign: 'center',
      lineHeight: 22,
      marginBottom: spacing.xs,
    },
    btn: {
      width: '100%',
      borderRadius: radius.md,
      paddingVertical: spacing.md,
      alignItems: 'center',
    },
    btnSecondary: {
      backgroundColor: 'transparent',
      borderWidth: 1,
    },
    btnText: {
      color: '#fff',
      fontSize: fontSize.md,
      fontWeight: '700',
    },
    laterBtn: { paddingVertical: spacing.sm },
    laterText: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
    },
  });
}
